import { Button, Col, Form, Input, message, Row, Select, Space, Spin, Switch } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash/get";
import assign from "lodash/assign";
import map from "lodash/map";
import flatMap from "lodash/flatMap";
import filter from "lodash/filter";
import findIndex from "lodash/findIndex";
import includes from "lodash/includes";

import { ADD_USER_SUCCESS, UPDATE_USER_SUCCESS } from "../../constants/message";
import { createUser, updateUser,getUserByID } from "../../redux/actions/userActions";
import { generateUserPayload, getUserCompanyIds } from "../../utilities/user";
import { USER_ROLES } from "../../constants/userRoles";
import { fetchOrganisationRoles } from "../../redux/actions/authActions";

const AddEditUser = ({ toggle, data, refresh, edit, companies }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const currentRoleId = useSelector((state) => get(state, "auth.user.role.id"));
  const dispatch = useDispatch();

  const initialCompanies = map(get(data, "companies", []), (company) =>
    get(company, "id", company)
  );

  const onCreateUser = (payload) => {
    setLoading(true);
    dispatch(createUser(payload))
      .then(() => {
        setLoading(false);
        message.success(ADD_USER_SUCCESS);
        refresh();
        toggle();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onUpdateUser = (payload) => {
    setLoading(true);
    payload = assign(payload, { version: get(data, 'version') });
    dispatch(updateUser(get(data, "id"), payload))
      .then(() => {
        setLoading(false);
        message.success(UPDATE_USER_SUCCESS);
        refresh();
        toggle();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onFinish = (values) => {
    const payload = generateUserPayload(values, companies);
    if (edit) {
      onUpdateUser(payload);
    } else {
      onCreateUser(payload);
    }
  };

  // USER_ROLES is ordered highest → lowest; a user may assign their own role or any role below it.
  // Roles outside the hierarchy (e.g. super admin) can assign every role.
  const getAssignableRoleIds = () => {
    const currentRoleIndex = findIndex(USER_ROLES, (userRole) => includes(get(userRole, "ids"), currentRoleId));
    return flatMap(currentRoleIndex === -1 ? USER_ROLES : USER_ROLES.slice(currentRoleIndex), "ids");
  };

  const getOrganisationRoles = () => {
    setLoading(true);
    dispatch(fetchOrganisationRoles())
      .then((result) => {
        const allowedRoleIds = getAssignableRoleIds();
        setRoles(filter(get(result, "list", []), (role) => includes(allowedRoleIds, get(role, "id"))));
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      })
  }

  const getUserDetails = (id) => {
    setLoading(true)
    dispatch(getUserByID(id)).then((result) => {
      form.setFieldsValue({
        name: get(result, 'name'),
        enabled: get(result, 'enabled'),
        roleId: get(result, 'role.id'),
        email: get(result, 'email'),
        locations: getUserCompanyIds(result, companies)
      })
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    })
  }

  useEffect(() => {
    getOrganisationRoles()
    if (edit) {
      
      getUserDetails(get(data, 'id'))
    }
  }, [])
  

  return (
    <div className="h-full flex flex-col">
      <Spin spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          onFinishFailed={() => { }}
          scrollToFirstError
          requiredMark={false}
          initialValues={
            edit
              ? { ...data, companies: initialCompanies }
              : {
                enabled: true,
              }
          }
        >
          <Row gutter={20}>
            <Col xl={24} xs={24}>
              <Form.Item
                label="Companies"
                name="locations"
                rules={[{ required: true, message: "Please select at least one Company...!" }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select Companies"
                  allowClear
                  maxTagCount="responsive"
                  optionFilterProp="label"
                  options={map(companies, (company) => ({
                    label: get(company, "name"),
                    value: get(company, "id"),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={20}>
            <Col xl={12} xs={24}>
              <Form.Item
                label="Full Name"
                name="name"
                rules={[{ required: true, message: "Please input Full Name...!" }]}
              >
                <Input placeholder="Full Name" />
              </Form.Item>
            </Col>
            <Col xl={12} xs={24}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Please input Email...!" },
                  { type: "email", message: "Please input a valid Email...!" },
                ]}
              >
                <Input placeholder="Email" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={20}>
            <Col xl={12} xs={24}>
              <Form.Item
                label="Role"
                name="roleId"
                rules={[{ required: true, message: "Please select Role...!" }]}
              >
                <Select
                  placeholder="Select Role"
                  options={map(roles, (role) => ({
                    label: get(role, "name"),
                    value: get(role, "id"),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xl={12} xs={24}>
              <Form.Item label="Status" name="enabled" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={null} className="flex justify-end space-x-2">
            <Space>
              <Button onClick={() => toggle()} className="border-gray-300">
                Close
              </Button>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </div>
  );
};

export default AddEditUser;
